import type { FramePoint, HoleShot } from '../api/public'
import { leftToPin, lieWords, shotLength } from './holeGeometry'

export interface ShotTablePlayer {
  name: string
  series: number
  gross: number
  shots: HoleShot[]
}

/**
 * Every shot on the hole: a row per PLAYER and a column per SHOT, so the field
 * is compared down a column -- everybody's tee shot, everybody's second -- the
 * way the hole was played. Each cell carries what the map's card gives: the
 * club, how far it went, where it finished and what was left.
 *
 * A player's shots may be fewer than their score -- a penalty stroke is a
 * stroke, not a swing, and a conceded putt is never struck -- so the score
 * stays at the start of the row and the shots are never offered as a recount
 * of it (FR-039c). A shot that came to rest in a penalty area is marked in its
 * own cell, which is where the stroke it cost was lost.
 */
export function ShotTable({
  players,
  pin,
}: {
  players: ShotTablePlayer[]
  pin: FramePoint | null
}) {
  const withShots = players.filter((p) => p.shots.length > 0)
  if (withShots.length === 0) return null
  const most = Math.max(...withShots.map((p) => Math.max(...p.shots.map((s) => s.shot_number))))
  const columns = Array.from({ length: most }, (_, i) => i + 1)

  return (
    <section className="shot-table" data-testid="shot-table">
      <h3>Shots</h3>
      <div className="scroll-x">
        <table className="table shot-grid">
          <thead>
            <tr>
              <th scope="col">Player</th>
              {columns.map((n) => (
                <th key={n} scope="col">
                  Shot {n}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {withShots.map((p) => {
              const byNumber = new Map(p.shots.map((s) => [s.shot_number, s]))
              return (
                <tr
                  key={p.name}
                  className={`series-${p.series % 5}`}
                  data-testid={`shot-row-${p.name}`}
                >
                  <th scope="row" className="shot-table-player">
                    <span className="swatch" aria-hidden="true" />
                    {p.name}
                    <span className="note"> · {p.gross}</span>
                  </th>
                  {columns.map((n) => {
                    const s = byNumber.get(n)
                    if (!s) {
                      // A gap straight after a penalty is the penalty STROKE --
                      // counted in the score, never swung -- and saying so keeps
                      // the blank from reading as missing data.
                      const afterPenalty = byNumber.get(n - 1)?.penalty
                      return afterPenalty ? (
                        <td key={n} className="shot-cell shot-cell-stroke">
                          <span className="note">penalty stroke</span>
                        </td>
                      ) : (
                        <td key={n} className="shot-cell-empty" aria-label="no shot" />
                      )
                    }
                    const left = leftToPin(s, pin)
                    return (
                      <td
                        key={n}
                        className={`shot-cell${s.penalty ? ' shot-cell-penalty' : ''}`}
                        data-testid={`shot-cell-${p.name}-${n}`}
                      >
                        <span className="shot-club">{s.club ?? '—'}</span>
                        <span className="shot-length">{shotLength(s)}</span>
                        <span className="note">
                          to {lieWords(s.lie_to)}
                          {s.penalty && <span className="shot-penalty"> · penalty</span>}
                        </span>
                        {left && (
                          <span className="note">
                            {left === 'holed' ? 'holed' : `${left} left`}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
